/**
 * Pure utility functions for file tree manipulation.
 *
 * @module
 */

/** A node in the file explorer tree. */
export interface FileNode {
  name: string
  path: string
  type: 'file' | 'directory'
  children?: FileNode[]
  gitStatus?: 'modified' | 'added' | 'deleted' | 'untracked'
  isDimmed?: boolean
  symlinkTarget?: string
}

/**
 * Recursively annotate FileNode trees with gitStatus from a status map.
 * Preserves node references when the status hasn't changed to avoid unnecessary re-renders.
 * @param nodes - The tree nodes to annotate.
 * @param statuses - Map of file path to git status string.
 * @returns The annotated tree, reusing old references where nothing changed.
 */
export function annotateGitStatus(nodes: FileNode[], statuses: Record<string, string>): FileNode[] {
  let changed = false
  const result = nodes.map((node) => {
    const newStatus = statuses[node.path] as FileNode['gitStatus']
    const newChildren = node.children ? annotateGitStatus(node.children, statuses) : undefined
    if (node.gitStatus === newStatus && node.children === newChildren) return node
    changed = true
    return { ...node, gitStatus: newStatus, children: newChildren }
  })
  return changed ? result : nodes
}

/**
 * Merge a freshly-fetched node list into the existing tree, preserving object
 * references for nodes that haven't changed so React can skip re-rendering them.
 * For directories whose children were previously loaded, the existing children
 * are kept (they'll be updated when their own directory is fetched).
 * @param oldNodes - The current tree nodes.
 * @param newNodes - The freshly-fetched tree nodes.
 * @returns The merged tree, reusing old references where nothing changed.
 */
export function mergeFileTree(oldNodes: FileNode[], newNodes: FileNode[]): FileNode[] {
  const oldMap = new Map(oldNodes.map((n) => [n.path, n]))
  let changed = false

  const merged = newNodes.map((next) => {
    const prev = oldMap.get(next.path)
    if (!prev) {
      changed = true
      return next
    }
    // Same basic identity — check if anything visible changed
    const sameLeaf =
      prev.name === next.name &&
      prev.type === next.type &&
      prev.isDimmed === next.isDimmed &&
      prev.gitStatus === next.gitStatus &&
      prev.symlinkTarget === next.symlinkTarget
    if (!sameLeaf) {
      changed = true
      // Preserve loaded children for directories
      return prev.children !== undefined && next.children === undefined
        ? { ...next, children: prev.children }
        : next
    }
    // For directories with loaded children, merge recursively if new children were fetched
    if (prev.children !== undefined && next.children !== undefined) {
      const mergedChildren = mergeFileTree(prev.children, next.children)
      if (mergedChildren === prev.children) return prev // unchanged subtree
      changed = true
      return { ...prev, children: mergedChildren }
    }
    // Carry forward previously loaded children when not re-fetched
    if (prev.children !== undefined && next.children === undefined) {
      return prev
    }
    return prev
  })

  // Check if any old nodes were removed
  if (merged.length !== oldNodes.length) changed = true
  if (!changed && merged.every((n, i) => n === oldNodes[i])) return oldNodes
  return merged
}

/**
 * Recursively inject children into a file tree at a given path.
 * @param nodes - The tree nodes to search.
 * @param targetPath - The directory path to inject children into.
 * @param children - The child nodes to inject.
 * @returns The updated tree with children injected at the target path.
 */
export function injectChildrenInTree(
  nodes: FileNode[],
  targetPath: string,
  children: FileNode[],
): FileNode[] {
  return nodes.map((node) => {
    if (node.path === targetPath) {
      return { ...node, children }
    }
    if (node.children) {
      return { ...node, children: injectChildrenInTree(node.children, targetPath, children) }
    }
    return node
  })
}
